import { Head, router } from "@inertiajs/react"
import { BarChart2 } from "lucide-react"
import { useState, useCallback, type ReactNode } from "react"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"

import { AdminLayout } from "@/layouts/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type {
  AnalyticsDateRange,
  ChannelBreakdown,
  DatePoint,
  DistributorPoiRow,
  OnboardingFunnelEntry,
  OnboardingStatus,
  PoiMetric,
  RegionMetric,
  RegionOption,
  TwoLayerBucket,
  TwoLayerMetrics,
} from "@/lib/types"

interface Props {
  sessions_over_time: DatePoint[]
  activation_channels: ChannelBreakdown[]
  onboarding_funnel: OnboardingFunnelEntry[]
  top_regions: RegionMetric[]
  top_pois_by_views: PoiMetric[]
  top_pois_by_directions: PoiMetric[]
  regions: RegionOption[]
  current_region_id: number | null
  date_range: AnalyticsDateRange
  is_distributor: boolean
  poi_performance: DistributorPoiRow[]
  two_layer_metrics: TwoLayerMetrics | null
}

const PRESET_DAYS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
] as const

const CHANNEL_COLORS: Record<string, string> = {
  qr_scan:       "#3b82f6",
  wifi_redirect: "#10b981",
  direct_link:   "#f59e0b",
  pdf_letter:    "#8b5cf6",
  unknown:       "#6b7280",
}

const CHART_COLOR = "#3b82f6"

const ONBOARDING_LABELS: Record<OnboardingStatus, string> = {
  pending:         "Pending",
  materials_sent:  "Materials Sent",
  wifi_configured: "WiFi Configured",
  live:            "Live",
}

function SectionHeader({ title }: { title: string }) {
  return (
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
    </CardHeader>
  )
}

function EmptyChart() {
  return (
    <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
      No data for this period
    </div>
  )
}

function HorizontalBarChart({
  data,
  nameKey,
}: {
  data: Array<{ count: number } & Record<string, unknown>>
  nameKey: string
}) {
  if (data.length === 0) return <EmptyChart />
  const chartHeight = Math.max(data.length * 40, 120)
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart layout="vertical" data={data} margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey={nameKey} tick={{ fontSize: 11 }} width={180} />
        <Tooltip />
        <Bar dataKey="count" fill={CHART_COLOR} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function BucketStat({
  bucket,
  label,
}: {
  bucket: TwoLayerBucket
  label: string
}) {
  return (
    <Card>
      <CardContent className="pt-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        {"insufficient_data" in bucket ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Insufficient data (need ≥3 properties per group)
          </p>
        ) : (
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {bucket.avg_sessions.toFixed(1)}
            <span className="ml-1 text-sm font-normal text-muted-foreground">avg sessions</span>
          </p>
        )}
        {"count" in bucket && (
          <p className="text-xs text-muted-foreground">{bucket.count} properties</p>
        )}
      </CardContent>
    </Card>
  )
}

function TwoLayerSection({ metrics }: { metrics: TwoLayerMetrics }) {
  const above = metrics.above_threshold
  const below = metrics.below_threshold

  const hasAboveData = !("insufficient_data" in above)
  const hasBelowData = !("insufficient_data" in below)

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-medium">Two-Layer Model validation</h2>
        <p className="text-xs text-muted-foreground">
          Hypothesis: regions with ≥{metrics.threshold} approved public POIs drive higher guest engagement
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <BucketStat
          bucket={above}
          label={`Regions ≥${metrics.threshold} POIs`}
        />
        <BucketStat
          bucket={below}
          label={`Regions <${metrics.threshold} POIs`}
        />
      </div>

      {(hasAboveData || hasBelowData) && (
        <Card>
          <SectionHeader title="Per-property breakdown" />
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead className="text-right">Region POIs</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hasAboveData &&
                  above.properties.map((p) => (
                    <TableRow key={p.property_id}>
                      <TableCell className="text-sm">{p.property_name}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{p.region_poi_count}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{p.session_count}</TableCell>
                    </TableRow>
                  ))}
                {hasBelowData &&
                  below.properties.map((p) => (
                    <TableRow key={p.property_id}>
                      <TableCell className="text-sm text-muted-foreground">{p.property_name}</TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {p.region_poi_count}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                        {p.session_count}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default function PlatformAnalyticsIndex({
  sessions_over_time,
  activation_channels,
  onboarding_funnel,
  top_regions,
  top_pois_by_views,
  top_pois_by_directions,
  regions,
  current_region_id,
  date_range,
  is_distributor,
  poi_performance,
  two_layer_metrics,
}: Props) {
  const [customFrom, setCustomFrom] = useState(date_range.from)
  const [customTo, setCustomTo] = useState(date_range.to)
  const [showCustom, setShowCustom] = useState(false)
  const [navigating, setNavigating] = useState(false)
  const [customError, setCustomError] = useState<string | null>(null)

  // Determine which preset (if any) matches the active date range so we can
  // highlight it. Compare day counts rather than exact strings because the
  // server may normalise the "to" date to today's date.
  const activeDays = (() => {
    const from = new Date(date_range.from)
    const to = new Date(date_range.to)
    const diff = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
    return PRESET_DAYS.find(({ days }) => days === diff)?.days ?? null
  })()

  const navigate = useCallback(
    (params: Record<string, string | undefined>) => {
      setNavigating(true)
      router.get(
        window.location.pathname,
        params,
        {
          preserveState: false,
          onFinish: () => setNavigating(false),
        },
      )
    },
    [],
  )

  function applyPreset(days: number) {
    setShowCustom(false)
    setCustomError(null)
    const to = new Date()
    const from = new Date()
    from.setDate(from.getDate() - days)
    const fromStr = from.toISOString().slice(0, 10)
    const toStr = to.toISOString().slice(0, 10)
    setCustomFrom(fromStr)
    setCustomTo(toStr)
    navigate({ from: fromStr, to: toStr, region_id: current_region_id?.toString() })
  }

  function applyCustom() {
    setCustomError(null)
    if (!customFrom || !customTo) {
      setCustomError("Please enter both a start and end date.")
      return
    }
    if (customFrom > customTo) {
      setCustomError("Start date must be on or before the end date.")
      return
    }
    navigate({ from: customFrom, to: customTo, region_id: current_region_id?.toString() })
  }

  function handleRegionChange(value: string) {
    navigate({
      from: date_range.from,
      to: date_range.to,
      region_id: value === "all" ? undefined : value,
    })
  }

  const hasAnyData =
    sessions_over_time.length > 0 ||
    activation_channels.length > 0 ||
    onboarding_funnel.length > 0 ||
    top_regions.length > 0 ||
    top_pois_by_views.length > 0 ||
    top_pois_by_directions.length > 0 ||
    (is_distributor && poi_performance.length > 0)

  const formattedFunnel = onboarding_funnel.map((item) => ({
    ...item,
    label: ONBOARDING_LABELS[item.status] ?? item.status,
  }))

  return (
    <>
      <Head title="Platform Analytics" />

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Platform Analytics</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Aggregated guest engagement across all properties
          </p>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-start gap-3">
          {/* Date presets */}
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Date range presets">
            {PRESET_DAYS.map(({ label, days }) => (
              <Button
                key={label}
                variant={activeDays === days ? "secondary" : "outline"}
                size="sm"
                onClick={() => applyPreset(days)}
                disabled={navigating}
                aria-pressed={activeDays === days}
              >
                {label}
              </Button>
            ))}
          </div>

          <Button
            variant={showCustom ? "secondary" : "outline"}
            size="sm"
            onClick={() => {
              setShowCustom((v) => !v)
              setCustomError(null)
            }}
            aria-expanded={showCustom}
          >
            Custom
          </Button>

          {showCustom && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="analytics-from" className="text-xs text-muted-foreground">
                  From
                </Label>
                <Input
                  id="analytics-from"
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomFrom(e.target.value)
                    setCustomError(null)
                  }}
                  className="h-8 w-36 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="analytics-to" className="text-xs text-muted-foreground">
                  To
                </Label>
                <Input
                  id="analytics-to"
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCustomTo(e.target.value)
                    setCustomError(null)
                  }}
                  className="h-8 w-36 text-sm"
                />
              </div>
              <Button
                size="sm"
                onClick={applyCustom}
                disabled={navigating || !customFrom || !customTo}
              >
                Apply
              </Button>
              {customError && (
                <p role="alert" className="w-full text-xs text-destructive">
                  {customError}
                </p>
              )}
            </div>
          )}

          {/* Region filter */}
          <Select
            value={current_region_id?.toString() ?? "all"}
            onValueChange={handleRegionChange}
            disabled={navigating || (is_distributor && regions.length === 0)}
          >
            <SelectTrigger className="h-8 w-48 text-sm" aria-label="Filter by region">
              <SelectValue placeholder={is_distributor ? "All managed regions" : "All regions"} />
            </SelectTrigger>
            <SelectContent>
              {/* Distributors are always scoped to managed regions — no "all" option needed */}
              {!is_distributor && (
                <SelectItem value="all">All regions</SelectItem>
              )}
              {is_distributor && regions.length === 0 && (
                <SelectItem value="none" disabled>No managed regions assigned</SelectItem>
              )}
              {regions.map((r) => (
                <SelectItem key={r.id} value={r.id.toString()}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {navigating && (
            <span className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
              Loading…
            </span>
          )}
        </div>

        {/* Distributor POI performance — shown first for distributor users */}
        {is_distributor && (
          <section className="mb-8">
            {/* Compact summary stats */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Total Curated POIs</p>
                <p className="text-2xl font-semibold">{poi_performance.length}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">With Engagement</p>
                <p className="text-2xl font-semibold">{poi_performance.filter(r => r.views > 0).length}</p>
              </Card>
              <Card className="p-4">
                <p className="text-sm text-muted-foreground">Top POI</p>
                <p className="text-lg font-medium truncate">{poi_performance[0]?.name ?? "—"}</p>
              </Card>
            </div>

            {/* Performance table */}
            {poi_performance.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No guest engagement recorded in this period.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>POI</TableHead>
                    <TableHead className="text-right">Views</TableHead>
                    <TableHead className="text-right">Directions</TableHead>
                    <TableHead className="text-right">Bookings</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead>Last Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {poi_performance.map(row => (
                    <TableRow key={row.poi_id}>
                      {/* POI name ALWAYS at full opacity */}
                      <TableCell className="font-medium">{row.name}</TableCell>
                      {/* Zero-engagement: mute the NUMBERS only, not the name */}
                      <TableCell className={`text-right ${row.views === 0 ? 'text-muted-foreground' : ''}`}>
                        {row.views === 0 ? '—' : row.views}
                      </TableCell>
                      <TableCell className={`text-right ${row.directions === 0 ? 'text-muted-foreground' : ''}`}>
                        {row.directions === 0 ? '—' : row.directions}
                      </TableCell>
                      <TableCell className={`text-right ${row.bookings === 0 ? 'text-muted-foreground' : ''}`}>
                        {row.bookings === 0 ? '—' : row.bookings}
                      </TableCell>
                      <TableCell>
                        {new Date(row.added_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.last_active_at
                          ? new Date(row.last_active_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>
        )}

        {/* Empty state */}
        {!hasAnyData && !navigating && (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <BarChart2 className="size-8 text-muted-foreground/40" aria-hidden="true" />
              <p className="font-medium">No engagement data yet</p>
              <p className="text-sm text-muted-foreground">
                Data will appear once guests start using guides. Try a wider date range.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Chart grid */}
        {(hasAnyData || navigating) && (
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Sessions over time */}
            <Card className="lg:col-span-2">
              <SectionHeader title="Guest sessions over time" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : sessions_over_time.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height={192}>
                    <LineChart data={sessions_over_time} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={CHART_COLOR}
                        strokeWidth={2}
                        dot={false}
                        name="Sessions"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Activation channels */}
            <Card>
              <SectionHeader title="Activation channels" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : activation_channels.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height={192}>
                    <PieChart>
                      <Pie
                        data={activation_channels}
                        dataKey="count"
                        nameKey="channel"
                        cx="50%"
                        cy="50%"
                        outerRadius={72}
                        label={false}
                      >
                        {activation_channels.map((entry) => (
                          <Cell key={entry.channel} fill={CHANNEL_COLORS[entry.channel] ?? "#6b7280"} />
                        ))}
                      </Pie>
                      <Legend
                        formatter={(value: string) => value.replace(/_/g, " ")}
                        wrapperStyle={{ fontSize: 11 }}
                      />
                      <Tooltip formatter={(value) => [typeof value === "number" ? value : 0, "Sessions"]} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Onboarding funnel */}
            <Card>
              <SectionHeader title="Onboarding funnel" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  <HorizontalBarChart data={formattedFunnel} nameKey="label" />
                )}
              </CardContent>
            </Card>

            {/* Two-Layer Model (admin only) */}
            {two_layer_metrics && (
              <Card className="lg:col-span-2">
                <CardContent className="pt-4">
                  <TwoLayerSection metrics={two_layer_metrics} />
                </CardContent>
              </Card>
            )}

            {/* Top regions table */}
            <Card>
              <SectionHeader title="Top regions by engagement" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : top_regions.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Region</TableHead>
                        <TableHead className="text-right">Events</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {top_regions.map((r) => (
                        <TableRow key={r.region_id}>
                          <TableCell className="text-sm">{r.name}</TableCell>
                          <TableCell className="text-right text-sm tabular-nums">{r.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Top POIs by views */}
            <Card>
              <SectionHeader title="Top POIs — views" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  <HorizontalBarChart data={top_pois_by_views} nameKey="name" />
                )}
              </CardContent>
            </Card>

            {/* Top POIs by directions */}
            <Card>
              <SectionHeader title="Top POIs — directions" />
              <CardContent>
                {navigating ? (
                  <Skeleton className="h-48 w-full" />
                ) : (
                  <HorizontalBarChart data={top_pois_by_directions} nameKey="name" />
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  )
}

PlatformAnalyticsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
